/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
import { useEffect, useState } from 'react';
import { QueryClient, QueryCache, MutationCache, QueryClientProvider, setLogger } from 'react-query';
import { persistQueryClient } from 'react-query/persistQueryClient-experimental';
import { createWebStoragePersistor } from 'react-query/createWebStoragePersistor-experimental';
import { ReactQueryDevtools } from 'react-query/devtools';
import { DrawerProvider } from '@lambdacurry/component-library';
import { Snackbar } from '@lambdacurry/component-library/dist/lib/snackbars';
import { useHandleQueryError, setAuthHeader } from 'lib/util';
import { IconProvider } from './src/components';
import { Store, useAuthStore } from './src/store';
import ReactModal from 'react-modal';

// Note: We import Tailwind separately to avoid rebuilding all of Tailwind whenever we change our custom global styles.
import './src/styles/tailwind.scss';
import './src/styles/global.scss';
import { navigate } from 'gatsby';
import { STOREFRONT_URL } from './src/util/constants';

// Note: prevents logging an error when the user is not logged in
setLogger({
  error: error => {
    if (
      error?.response?.errors &&
      error.response.errors.find(error => error.message.includes('Authorization header not found.'))
    ) {
      return null;
    }
    console.error(error);
  }
});

// Note: prevents error/warning with ReactModal
ReactModal.setAppElement('body');

const PageWrapper = ({ children }) => {
  const clearToken = useAuthStore(store => store.clearToken);
  const { handleQueryError } = useHandleQueryError({ onTokenInvalid: clearToken });

  const [queryClient] = useState(
    new QueryClient({
      queryCache: new QueryCache({
        onError: (error, query) => {
          // SEE: https://tkdodo.eu/blog/react-query-error-handling#the-global-callbacks
          // 🎉 only show error snackbars if we already have data in the cache
          // which indicates a failed background update
          handleQueryError(error);
        }
      }),
      mutationCache: new MutationCache({
        onError: (error, query) => {
          // SEE: https://tkdodo.eu/blog/react-query-error-handling#the-global-callbacks
          // 🎉 only show error snackbars if we already have data in the cache
          // which indicates a failed background update
          handleQueryError(error);
        }
      })
    })
  );

  useEffect(() => {
    // Make sure we are on the correct url when hosted in netlify.
    //
    // QUESTION: Do we not have flags that we can check (i.e., ENV === 'production'),
    // rather than having to check the domain name, since that *could* change?
    if (STOREFRONT_URL && STOREFRONT_URL.includes('my.nibll.com') && !STOREFRONT_URL.includes(window.location.origin)) {
      return navigate(STOREFRONT_URL);
    }

    const localStoragePersistor = createWebStoragePersistor({ storage: window.localStorage });
    persistQueryClient({
      queryClient,
      persistor: localStoragePersistor
    });
  }, []);

  return (
    <Store providers={[IconProvider, DrawerProvider]}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Store>
  );
};

const AuthWrapper = ({ children }) => {
  const { token } = useAuthStore();

  // NOTE: Putting this inside a `useEffect` prevents the auth header from being set on
  // first render, causing redirection to the login page, even if logged in.
  setAuthHeader(token);

  return children;
};

export const wrapPageElement = ({ element }) => (
  <AuthWrapper>
    <Snackbar.Provider>
      <PageWrapper>{element}</PageWrapper>
    </Snackbar.Provider>
  </AuthWrapper>
);
